import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Gift, Shield } from "lucide-react";
import { motion } from "framer-motion";
import { getCategoryColor } from "@/lib/categoryColors";
import { useOnboardingHints } from "@/hooks/useOnboardingHints";
import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { BrandLogo } from "./BrandLogo";
import { UnifiedStatusBadge } from "./UnifiedStatusBadge";
import { getValueDisplay } from "./offerHelpers";
import { DietaryPills } from "./DietaryPills";
import AvailabilityPill from "./AvailabilityPill";
import UpdatedBadge from "./UpdatedBadge";
import ExpiringSoonBadge from "./ExpiringSoonBadge";
import WaitTimeDisplay from "./WaitTimeDisplay";
import WaitTimeSheet from "./WaitTimeSheet";

/* Onboarding hint overlays */
const PulsingDot = () => (
  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 z-10 pointer-events-none">
    <span className="absolute inset-0 rounded-full bg-sky-400 animate-ping opacity-75" />
    <span className="absolute inset-0 rounded-full bg-sky-500" />
  </span>
);

const ShimmerOverlay = () => (
  <div className="absolute inset-0 rounded-lg overflow-hidden pointer-events-none z-10">
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_7s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
  </div>
);

export const OfferCard = ({ offer, onClick, userCity, isSelected, isFavorited, onToggleFavorite, isWishlisted, onToggleWishlist, onShare, showHints, showBoycott = false }) => {
  const navigate = useNavigate();
  const hints = useOnboardingHints();
  const { getStatus } = useUnifiedStatus();
  const dealStatus = getStatus(offer.business_id);
  const [showWaitSheet, setShowWaitSheet] = useState(false);
  const cc = getCategoryColor(offer.category);
  const nearestLocation = offer.nearest_location;
  const hasMultipleLocations = offer.locations && offer.locations.length > 1;
  const locationDisplay = nearestLocation
    ? `${nearestLocation.city}, ${nearestLocation.state}${hasMultipleLocations ? ` +${offer.locations.length - 1}` : ""}`
    : (offer.coverage === "Nationwide" ? "Nationwide" : `${offer.city || ""}, ${offer.state || ""}`.replace(/^, |, $/g, '') || "See website");

  const vd = getValueDisplay(offer);
  const closesIn = offer.hours_status?.minutes_until_close;
  const closesLabel = closesIn != null
    ? closesIn < 180 ? `${Math.round(closesIn / 60)}h left` : `${Math.round(closesIn / 60)}h`
    : offer.hours_status?.status_label || "—";
  const closesUrgent = closesIn != null && closesIn < 180;
  const confidence = offer.confidence_score || offer.verification_count || 0;
  const claimed = offer.times_claimed || offer.claim_count || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} whileTap={{ scale: 0.98 }} onClick={onClick}
      className="rounded-2xl overflow-hidden bg-white border border-slate-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.06)] cursor-pointer"
      data-testid={`offer-card-${offer.business_id}`}
    >
      {/* 1. HEADER — category gradient */}
      <div className={`${cc.gradient} px-3.5 py-2.5 flex items-center gap-2.5`}>
        <BrandLogo name={offer.name} logoUrl={offer.logo_url} size="sm" cuisineTag={offer.cuisine_tags?.[0] || offer.category} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white truncate leading-tight">{offer.name}</p>
          <p className="text-[11px] text-white/70 truncate">{offer.category} · {locationDisplay}</p>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onToggleFavorite?.(offer.business_id); }} className="p-1" data-testid={`favorite-btn-${offer.business_id}`}>
          <Heart className={`w-4.5 h-4.5 ${isFavorited ? "fill-red-400 text-red-400" : "text-white/60"}`} />
        </button>
      </div>

      {/* 2. OFFER ROW */}
      <div className="px-3.5 pt-2.5 pb-1.5 flex items-center justify-between gap-2">
        <p className="text-[13px] font-bold text-slate-800 line-clamp-1 flex-1">{offer.offer_title || "Birthday deal"}</p>
        {vd && (
          <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold flex-shrink-0" data-testid={`value-pill-${offer.business_id}`}>
            ~{vd.value}
          </span>
        )}
        {dealStatus && <UnifiedStatusBadge status={dealStatus} />}
      </div>

      {/* 3. PILLS ROW */}
      <div className="px-3.5 pb-2 flex flex-wrap gap-1">
        <AvailabilityPill offer={offer} />
        <UpdatedBadge freshness={offer.freshness} />
        <ExpiringSoonBadge daysLeft={offer.deal_status === "active" ? offer.deal_expires_in : null} />
        {/* iter265.1 — Franchise location signal at the list level so users
            see the disclaimer before tapping into the detail page. Renders
            only when offer.offer_confidence_note is set (Phase 6 stamps it
            on inherited offers from KNOWN_FRANCHISE_BRANDS). */}
        {offer.offer_confidence_note && (
          <span
            className="px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded text-[10px] font-semibold inline-flex items-center gap-0.5"
            data-testid={`verify-location-chip-${offer.business_id}`}
            title={offer.offer_confidence_note}
          >
            <Shield className="w-2.5 h-2.5" />Verify with location
          </span>
        )}
        {offer.is_true_freebie && <span className="px-1.5 py-0.5 bg-teal-50 text-teal-600 border border-teal-200 rounded text-[10px] font-semibold inline-flex items-center gap-0.5" data-testid={`true-freebie-${offer.business_id}`}><Gift className="w-2.5 h-2.5" />True Freebie</span>}
        <DietaryPills dietary={offer.dietary} boycottFlag={offer.boycott_flag} showBoycott={showBoycott} compact />
        {offer.offer_requires_app && <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded text-[10px] font-semibold">Requires App</span>}
        {offer.offer_requires_app === false && <span className="px-1.5 py-0.5 bg-purple-50 text-purple-600 border border-purple-200 rounded text-[10px] font-semibold">No App</span>}
        {(offer.offer_signup_lead_days || 0) > 0 && <span className="px-1.5 py-0.5 bg-amber-50 text-amber-600 border border-amber-200 rounded text-[10px] font-semibold">Requires Signup</span>}
        {offer.min_spend > 0 && <span className="px-1.5 py-0.5 bg-orange-50 text-orange-600 border border-orange-200 rounded text-[10px] font-semibold">${offer.min_spend} min</span>}
      </div>

      {/* WAIT TIME */}
      {offer.wait_time_reported_at && (
        <div className="px-3.5 pb-2">
          <WaitTimeDisplay
            waitMinutes={offer.wait_time_reported}
            waitCategory={offer.wait_time_category}
            reportedAt={offer.wait_time_reported_at}
            reporterCount={offer.wait_time_reported_by_count}
            onTap={() => setShowWaitSheet(true)}
            compact
          />
        </div>
      )}

      {/* 4. STATS BAR */}
      <div className="px-3.5 py-2 border-t border-slate-100 flex items-center justify-between text-[11px]">
        <div className="flex items-center gap-1">
          <span className="text-slate-400 font-medium">Confidence</span>
          <span className={`font-bold ${cc.text}`}>{confidence > 0 ? `${confidence}%` : "—"}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-slate-400 font-medium">{closesIn != null ? "Closes" : "Hours"}</span>
          <span className={`font-bold ${closesUrgent ? "text-red-500" : cc.text}`}>{closesLabel}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-slate-400 font-medium">Claimed</span>
          <span className={`font-bold ${cc.text}`}>{claimed > 0 ? claimed : "—"}</span>
        </div>
      </div>

      {showWaitSheet && (
        <WaitTimeSheet
          businessId={offer.business_id}
          businessName={offer.name}
          onClose={() => setShowWaitSheet(false)}
        />
      )}
    </motion.div>
  );
};
